import { format as formatDateFNS } from 'date-fns';

// Store for language settings
const languageStore = { language: 'en-gb' };

// Get the user's current timezone
const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

/**
 * Converts milliseconds to seconds.
 * @param timestamp - The time in milliseconds.
 * @returns The time in seconds.
 * @example
 * msToSeconds(10000); // returns 10
 */
export const msToSeconds = (timestamp: number) => timestamp / 1000;

/**
 * Converts a date string to a time string in the format "HH:MM".
 * @param date - The date string to convert.
 * @returns The formatted time string.
 * @example
 * getTime('2024-01-18T15:30:00'); // returns "15:30"
 */
export const getTime = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone
  });
};

/**
 * Formats a date string into a more readable format with month name.
 * @param date - The date string to format.
 * @returns The formatted date string.
 * @example
 * getPrettyDate('2024-01-18'); // returns "Jan 18, 2024"
 */
export const getPrettyDate = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleString(languageStore.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone
  });
};

/**
 * Converts a date string to a simple date format "YYYY-MM-DD".
 * @param date - The date string to format.
 * @returns The formatted date string.
 * @example
 * getSimpleDate('2024-01-18'); // returns "2024-01-18"
 */
export const getSimpleDate = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleString(languageStore.language, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone
  });
};

/**
 * Formats a date string to include time.
 * @param date - The date string to format.
 * @returns The formatted date and time string.
 * @example
 * getDateWithTime('2024-01-18T15:30:00'); // returns "January 18, 15:30"
 */
export const getDateWithTime = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleString(languageStore.language, {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZone
  });
};

/**
 * Formats a date to the format "DD MMM YYYY, HH:MM".
 * Accepts a Date object or a string. If a string is provided, it creates a new Date.
 * @param date - The date to format (Date object or string).
 * @returns The formatted date string.
 * @example
 * getPrettyDateWithTime('2023-12-19T20:58:00'); // returns "19 Dec 2023, 20:58"
 * getPrettyDateWithTime(new Date('2023-12-19T20:58:00')); // returns "19 Dec 2023, 20:58"
 */
export const getPrettyDateWithTime = (date: Date | string | undefined) => {
  if (!date) return 'unknown date';

  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  if (!isValidDate(parsedDate)) return undefined;

  return parsedDate.toLocaleString(languageStore.language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone
  });
};

/**
 * Gets the day of the week from a date string.
 * @param date - The date string.
 * @returns The name of the day.
 * @example
 * getDay('2024-01-18'); // returns "Friday"
 */
export const getDay = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleString(languageStore.language, {
    weekday: 'long',
    timeZone
  });
};

/**
 * Adds a specified number of days to a date.
 * @param date - The original date.
 * @param days - The number of days to add.
 * @returns The new date.
 * @example
 * addDays(new Date('2024-01-18'), 5); // returns new Date('2024-01-23')
 */
export const addDays = (date: Date, days: number) => {
  date.setDate(date.getDate() + days);
  return date;
};

/**
 * Converts milliseconds to a timestamp in the format "HH:MM:SS" or "MM:SS".
 * @param milliseconds - The time in milliseconds.
 * @returns The formatted timestamp.
 * @example
 * msToPrettyTimestamp(3661000); // returns "1:01:01"
 */
export const msToPrettyTimestamp = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  if (hours > 0) return `${hours}:${paddedMinutes}:${paddedSeconds}`;

  return `${paddedMinutes}:${paddedSeconds}`;
};

/**
 * Checks if the given object is a valid date.
 * @param date - The object to check.
 * @returns `true` if the object is a valid date, otherwise `false`.
 * @example
 * isValidDate(new Date('2024-01-18')); // returns true
 * isValidDate(new Date('invalid')); // returns false
 */
export const isValidDate = (date: Date) =>
  date instanceof Date && !Number.isNaN(Number(date));

/**
 * Formats a date according to the specified format.
 * Accepts a Date object or a string. If a string is provided, it creates a new Date.
 * @param date - The date to format (Date object or string).
 * @param format - The format string.
 * @returns The formatted date string or undefined if the date is invalid.
 * @example
 * formatDate('2024-01-18', 'yyyy-MM-dd'); // returns "2024-01-18"
 * formatDate(new Date('2024-01-18'), 'yyyy-MM-dd'); // returns "2024-01-18"
 */
export const formatDate = (date: Date | string | null, format: string) => {
  if (!date) return undefined;

  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  if (!isValidDate(parsedDate)) return undefined;

  return formatDateFNS(parsedDate, format);
};

/**
 * Pads a number with leading zeros to a specified size.
 *
 * @param number - The number to pad.
 * @param size - The desired size of the padded number (default is 2).
 * @returns The padded number as a string.
 */
export const padNumber = (number: number, size = 2) => {
  let s = String(number);

  while (s.length < size) {
    s = `0${s}`;
  }
  return s;
};

/**
 * Converts seconds to a timestamp in the format "MM:SS".
 * @param second - The time in seconds.
 * @returns The formatted timestamp.
 * @example
 * getMinuteTimestamp(65); // returns "01:05"
 */
export const getMinuteTimestamp = (second: number) => {
  const currentMinute = Math.floor(second / 60);
  const currentSecond = second - currentMinute * 60;
  return `${padNumber(currentMinute)}:${padNumber(currentSecond)}`;
};

/**
 * Converts seconds to a formatted string displaying only 2 units maximum.
 * @param seconds - The time in seconds.
 * @returns The formatted time string.
 * @example
 * secondsToDaysHoursAndMinutes(90061); // returns "1d & 1h"
 * secondsToDaysHoursAndMinutes(3661); // returns "1h & 01min"
 * secondsToDaysHoursAndMinutes(120); // returns "02min"
 * secondsToDaysHoursAndMinutes(45); // returns "01min"
 */
export const secondsToDaysHoursAndMinutes = (seconds: number) => {
  if (!seconds || seconds < 0) return '0min';

  const days = Math.floor(seconds / 86400); // 24 * 60 * 60
  const remainingAfterDays = seconds % 86400;
  const hours = Math.floor(remainingAfterDays / 3600);
  const remainingAfterHours = remainingAfterDays % 3600;
  const minutes = Math.floor(remainingAfterHours / 60);

  // If there are days, show only days & hours
  if (days > 0) {
    if (hours > 0) {
      return `${days}d & ${hours}h`;
    }
    return `${days}d`;
  }

  // If there are hours but no days, show hours & minutes
  if (hours > 0) {
    if (minutes > 0) {
      const paddedMinutes = String(minutes).padStart(2, '0');
      return `${hours}h & ${paddedMinutes}min`;
    }
    return `${hours}h`;
  }

  // If less than an hour, show only minutes
  if (minutes > 0) {
    const paddedMinutes = String(minutes).padStart(2, '0');
    return `${paddedMinutes}min`;
  }

  // If less than 1 minute, show 0min
  return '0min';
};

/**
 * Calculates the number of months elapsed between a past date and today.
 * @param date - The past date to compare (Date object or string).
 * @returns The number of months elapsed since the given date, or undefined if the date is invalid.
 * @example
 * getMonthsSince('2024-01-23'); // returns 15 (if today is 2025-04-23)
 */
export const getMonthsSince = (
  date: Date | string | undefined
): number | undefined => {
  if (!date) return undefined;

  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  if (!isValidDate(parsedDate)) return undefined;

  const today = new Date();

  return (
    (today.getFullYear() - parsedDate.getFullYear()) * 12 +
    (today.getMonth() - parsedDate.getMonth())
  );
};

export const formatVideoLength = (seconds: number): string => {
  if (Number.isNaN(seconds) || seconds < 0) return 'N/A';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  if (h > 0) return `${padNumber(h)}h ${padNumber(m)}m ${padNumber(s)}s`;
  if (m > 0) return `${padNumber(m)}m ${padNumber(s)}s`;
  return `${padNumber(s)}s`;
};

/**
 * Pluralizes a unit label based on a value, appending "ago".
 * @param value - The numeric quantity.
 * @param unit - The singular unit name (e.g. "day", "hour").
 * @returns A string like "1 day ago" or "5 days ago".
 */
const pluralize = (value: number, unit: string) =>
  `${value} ${unit}${value === 1 ? '' : 's'} ago`;

/**
 * Converts a date into a human-readable "time ago" string, relative to now.
 * Accepts a Date object or a string. If a string is provided, it creates a new Date.
 * @param date - The date to compare against now (Date object or string).
 * @returns A relative time string, or 'unknown date' if the date is missing/invalid.
 * @example
 * getTimeAgo('2024-01-13T00:00:00'); // returns "5 days ago" (if today is 2024-01-18)
 * getTimeAgo(new Date(Date.now() - 60000)); // returns "1 minute ago"
 */
export const getTimeAgo = (date: Date | string | undefined): string => {
  if (!date) return 'unknown date';

  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  if (!isValidDate(parsedDate)) return 'unknown date';

  const diffSeconds = Math.floor((Date.now() - parsedDate.getTime()) / 1000);
  if (diffSeconds < 60) return 'just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return pluralize(diffMinutes, 'minute');

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return pluralize(diffHours, 'hour');

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return pluralize(diffDays, 'day');

  return getPrettyDateWithTime(parsedDate) ?? 'unknown date';
};
